/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import {
    AutocompleteChangeReason,
    Avatar,
    Box,
    Grid,
    PopperProps,
    Popper,
    useMediaQuery,
    styled,
    useTheme,
    BoxProps,
} from "@mui/material";
import IotVisionAutoComplete from "components/common/IotVisonAutoComplete";
import { getIncidentFilterDropDownValues } from "api/app-taxi-ranking/taxi-incidents.api";
import CommonDateRangePicker, { DatePickerValue, pickerRanges } from "components/common/CommonDateRangePicker";
import IotVisionButton from "components/common/IotVisionButton";
import IotVisionTextFiled from "components/common/IotVisionTextFiled";
import { URLSearchParamsInit, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { filterUsers, findDropdownOptionFromList, moveItemToFirst } from "utils/data-manipulation";
import { useAuth0 } from "@auth0/auth0-react";
import { Assignee } from "types";
import { IncidentType, PresetFilterLabels, PresetFilterTypeOptions } from "enums/incident.enum";
import { isValidISODate } from "utils/date-format";
import IotVisionToolTips from "components/common/IotVisionToolTips";
import { WARNING_DATE_FILTERING_DISABLED_BY_APPLIED_PRE_FILTER } from "constants/validation.constants";

// Constant definitions
const PLACEHOLDER_INCIDENT_TYPE = "Incident Type";
const PLACE_HOLDER_PRESET_FILTERS = "Preset Filters";
const TOOLTIP_VISIBLE_TIME_ON_MOBILE = 5000;
const REGEX_DIGITS_ONLY_PATTERN = /^\d+$/;

export const configuredPreFilterValues: Record<string, string> = {
    [PresetFilterTypeOptions.EXCLUDE_REGO_WITH_INCIDENT_TYPE]:
        `${PresetFilterTypeOptions.EXCLUDE_REGO_WITH_INCIDENT_TYPE}:T:${IncidentType.CAR_IN_TAXIRANK}`,
    [PresetFilterTypeOptions.ONLY_REGO_WITH_INCIDENT_TYPE]:
        `${PresetFilterTypeOptions.ONLY_REGO_WITH_INCIDENT_TYPE}:T:${IncidentType.CAR_IN_TAXIRANK}`,
    [PresetFilterTypeOptions.EXPIRING_WITHIN_SEVEN_DAYS]: PresetFilterTypeOptions.EXPIRING_WITHIN_SEVEN_DAYS,
};

export const presetFilterOptions: Options = {
    name: PLACE_HOLDER_PRESET_FILTERS,
    value: [
        {
            id: `${PresetFilterTypeOptions.EXCLUDE_REGO_WITH_INCIDENT_TYPE}`,
            label: PresetFilterLabels.EXCLUDE_T_PLATE_IN_CAR_IN_TAXIRANK,
        },
        {
            id: `${PresetFilterTypeOptions.ONLY_REGO_WITH_INCIDENT_TYPE}`,
            label: PresetFilterLabels.ONLY_T_PLATE_IN_CAR_IN_TAXIRANK,
        },
        {
            id: `${PresetFilterTypeOptions.EXPIRING_WITHIN_SEVEN_DAYS}`,
            label: PresetFilterLabels.EXPIRING_WITHIN_SEVEN_DAYS,
        },
    ],
};

const defaultFilterValues: Record<string, string | DropdownValue | DatePickerValue | AutoLabelAssignee> = {
    altIdentificationNo: "",
    groupId: { label: "", id: "" },
    status: { label: "", id: "" },
    locationId: { label: "", id: "" },
    incident: "",
    idInc: "",
    dateTime: {
        fromTime: "",
        toTime: "",
        range: pickerRanges.DateRange,
    },
    assignedUserID: { name: "", user_id: "", email: "", picture: "" },
    preFilter: { label: "", id: "" },
};

// Interface definitions
interface DropdownValue {
    id: string;
    label: string;
}
interface OptionsLgaOrLocation {
    name: string;
    value: DropdownValue[];
}

const incidentOptions: Options = {
    name: "",
    value: [],
};

const statusOptions: Options = {
    name: "",
    value: [],
};

const assigneeOptions: Options = {
    name: "",
    value: [],
};
interface IncidentsPageFilterPanelProps {
    locationOptions: OptionsLgaOrLocation;
    lgaOptions: OptionsLgaOrLocation;
    isAllLocationEnable?: boolean;
    isRegionEnable?: boolean;
    isLgaEnable?: boolean;
    onFiltersReady: (status: boolean) => void;
}
interface Options {
    name: string;
    value: any[];
}
interface AutoLabelId {
    label: string;
    id: string;
}

interface AutoLabelAssignee {
    name: string;
    // eslint-disable-next-line camelcase
    user_id: string;
    picture: string;
    email: string;
}
interface IncidentFilterFormField {
    dateTime?: DatePickerValue;
    altIdentificationNo?: string;
    groupId?: AutoLabelId;
    status?: AutoLabelId;
    locationId?: AutoLabelId;
    incident?: string;
    idInc?: string;
    assignedUserID?: AutoLabelAssignee;
    preFilter?: AutoLabelId;
}
interface dropDownValue {
    name: string;
    value: string[];
}

interface BoxWrapperProps extends BoxProps {
    isDisabled?: boolean;
}

interface SearchParamTypes extends IncidentFilterFormField {
    pageIndex?: string;
}

// Custom component definitions
const StyledAutocompletePopper = styled((popperProps: PopperProps) => {
    const theme = useTheme();
    const isBelowLgScreen = useMediaQuery(theme.breakpoints.down("lg"));
    return (
        <Popper
            {...popperProps}
            style={{
                width: "fit-content",
            }}
            placement={isBelowLgScreen ? "bottom" : "bottom-start"}
        />
    );
})(({ theme }) => {
    const isXlScreen = useMediaQuery(theme.breakpoints.up("xl"));
    return {
        "& .MuiAutocomplete-listbox": {
            height: "100%",
            maxHeight: isXlScreen ? "600px" : "200px",
        },
    };
});

const StyledDisableWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== "isDisabled" })<BoxWrapperProps>(
    ({ isDisabled }) => ({
        display: "inline-block",
        width: "100%",
        pointerEvents: "auto",
        "& .MuiOutlinedInput-notchedOutline": {
            borderColor: isDisabled ? "rgba(0, 0, 0, 0.15) !important" : "rgba(0, 0, 0, 0.23)",
        },
    }),
);

const StyledBoxWrapper = styled(Box, { shouldForwardProp: (prop) => prop !== "isDisabled" })<BoxWrapperProps>(
    ({ isDisabled }) => ({
        pointerEvents: isDisabled ? "none" : "auto",
    }),
);

// Implementation of the FilterPanel component
const IncidentsPageFilterPanel: React.FC<IncidentsPageFilterPanelProps> = ({
    locationOptions,
    lgaOptions,
    isAllLocationEnable,
    isLgaEnable,
    isRegionEnable,
    onFiltersReady,
}) => {
    const [dropDownTypesValues, setDropDownTypes] = React.useState<Options[]>([]);
    const [disableWarning, setDisableWarning] = React.useState<string | null>(null);
    const [tooltipOpen, setTooltipOpen] = React.useState<boolean>(false);
    const { groupId: groupUrlId } = useParams();
    const { user } = useAuth0();
    const [searchParams, setSearchParams] = useSearchParams();
    const filtersAbortControllerRef = useRef<AbortController | null>(null);
    const wrapperRef = useRef<HTMLSpanElement>(null);
    const {
        altIdentificationNo,
        groupId,
        fromTime,
        toTime,
        range,
        status,
        locationId,
        incident,
        assignedUserID,
        idInc,
        selectedAssignee,
        preFilter,
    } = Object.fromEntries([...searchParams]) || {};
    const [pickerValue, setPickerValue] = useState<DatePickerValue>({
        fromTime: "",
        toTime: "",
        range: pickerRanges.DateRange,
    });

    const {
        handleSubmit,
        setValue,
        control,
        reset,
        trigger,
        formState: { isDirty, isValid },
    } = useForm<IncidentFilterFormField>({
        defaultValues: defaultFilterValues,
    });
  
    // Close tooltip on click/touch outside
    useEffect(() => {  
      if (tooltipOpen) {
        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("touchstart", handleClickOutside);
      }
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
        document.removeEventListener("touchstart", handleClickOutside);
      };
    }, [tooltipOpen]);

    useEffect(() => {
        dropDownTypesValues.forEach((type: dropDownValue) => defineDropDownValues(type));
    }, [dropDownTypesValues]);

    useEffect(() => {
        onFiltersReady(false);
        const foundGroupId = findDropdownOptionFromList(lgaOptions.value, groupId, "id");
        const foundStatus = findDropdownOptionFromList(statusOptions.value, status, "id");
        const foundLocationId = findDropdownOptionFromList(locationOptions.value, locationId, "id");
        const foundAssignedUser = findDropdownOptionFromList(assigneeOptions.value, assignedUserID, "user_id");
        const foundPreFilter = findDropdownOptionFromList(presetFilterOptions.value, preFilter, "id");
        const dateTime = isValidDateAndRange(fromTime, toTime, range as pickerRanges)
            ? { fromTime: fromTime || "", toTime: toTime || "", range: range || pickerRanges.DateRange }
            : { fromTime: "", toTime: "", range: pickerRanges.DateRange };
        const validFilters = {
            altIdentificationNo: altIdentificationNo || "",
            groupId: foundGroupId || { label: "", id: "" },
            status: foundStatus || { label: "", id: "" },
            locationId: foundLocationId || { label: "", id: "" },
            incident: incidentOptions.value.includes(incident) ? incident : "",
            idInc: idInc || "",
            dateTime,
            assignedUserID: foundAssignedUser || { name: "", user_id: "", email: "", picture: "" },
            preFilter: foundPreFilter || { label: "", id: "" },
        };
        if (dropDownTypesValues.length) {
            const { generatedSearchParams, nonFilterSearchParams } = getApplicableSearchParams(
                searchParams,
                validFilters,
            );
            setSearchParams({ ...nonFilterSearchParams, ...generatedSearchParams });
            onFiltersReady(true);
        }
        reset(validFilters);
        handlePreFilterSelection(validFilters.preFilter);
        setPickerValue(dateTime);
    }, [searchParams, dropDownTypesValues]);

    useEffect(() => {
        getDropDownTypesValues();
        return () => {
            filtersAbortControllerRef.current?.abort();
        };
    }, [groupUrlId, groupId, locationId, assignedUserID]);
    
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(event.target as Node)
        ) {
          setTooltipOpen(false);
        }
    };

    useEffect(() => {
        if (selectedAssignee) {
            const availableAssigneeOptions = assigneeOptions.value.map((option) => option.email);

            if (availableAssigneeOptions.includes(selectedAssignee)) {
                removeSelectedAssigneeParam();
            } else {
                getDropDownTypesValues();
            }
        }
    }, [selectedAssignee]);

    const handlePreFilterSelection = (filter?: AutoLabelId) => {
        switch (filter?.id) {
            case PresetFilterTypeOptions.EXPIRING_WITHIN_SEVEN_DAYS:
                setDisableWarning(WARNING_DATE_FILTERING_DISABLED_BY_APPLIED_PRE_FILTER);
                break;
            default:
                setDisableWarning(null);
        }
    };

    const removeSelectedAssigneeParam = () => {
        const queryParams = Object.fromEntries([...searchParams]);
        delete queryParams?.selectedAssignee;
        setSearchParams({ ...queryParams });
    };

    const isValidDateAndRange = (from: string, to: string, range: pickerRanges) => {
        if (!isValidISODate(from) || !isValidISODate(to) || !Object.values(pickerRanges).includes(range)) return false;
        if (new Date(to).getTime() <= new Date(from).getTime()) return false;
        return true;
    };

    const defineDropDownValues = (type: Options) => {
        switch (type.name) {
            case "Incident":
                incidentOptions.name = PLACEHOLDER_INCIDENT_TYPE;
                incidentOptions.value = type.value;
                break;
            case "Status":
                statusOptions.name = type.name;
                statusOptions.value = type.value;
                break;
            case "Assignee":
                assigneeOptions.name = type.name;
                assigneeOptions.value = moveItemToFirst<Assignee>(
                    filterUsers(type.value),
                    (assignee: Assignee) => assignee.user_id === user?.sub,
                );
                break;
            default:
        }
    };

    const getDropDownTypesValues = async () => {
        try {
            filtersAbortControllerRef.current = new AbortController();
            const { signal } = filtersAbortControllerRef.current;
            const response = await getIncidentFilterDropDownValues({ signal });
            if (response !== null && Array.isArray(response)) {
                removeSelectedAssigneeParam();
                setDropDownTypes(response);
            }
        } catch (e) {
            console.log(e);
        }
    };

    const handleClear = (): void => {
        reset({
            altIdentificationNo: "",
            groupId: { label: "", id: "" },
            status: { label: "", id: "" },
            locationId: { label: "", id: "" },
            incident: "",
            idInc: "",
            dateTime: {
                fromTime: "",
                toTime: "",
                range: pickerRanges.DateRange,
            },
            assignedUserID: { name: "", user_id: "", email: "", picture: "" },
        });
        setPickerValue({
            fromTime: "",
            toTime: "",
            range: pickerRanges.DateRange,
        });
        setSearchParams({
            pageIndex: "1",
        });
        setDisableWarning(null);
    };

    const checkFilterValidity = (data: SearchParamTypes): SearchParamTypes => {
        const validatedData = { ...data };
        if (validatedData?.preFilter?.id === PresetFilterTypeOptions.EXPIRING_WITHIN_SEVEN_DAYS)
            delete validatedData.dateTime;
        return validatedData;
    };

    const handleTooltipStatus = (status: boolean) => {
        setTooltipOpen(status);
    };

    const getApplicableSearchParams = (
        searchParameters: URLSearchParams,
        validFilters: Record<string, string | DropdownValue | DatePickerValue>,
    ) => {
        const { dateTime, ...rest } = defaultFilterValues;
        const validFilterKeys = {...rest, ...(dateTime as DatePickerValue)}
        const currentSearchParams = Object.fromEntries([...searchParameters]) || {};
        const nonFilterSearchParams = Object.keys(currentSearchParams).reduce(
            (result: Record<string, string | string[]>, key: string) => {
                if (!(key in validFilterKeys)) result[key] = currentSearchParams[key];
                return result;
            },
            {},
        );
        const { pageIndex: pageNumber } = currentSearchParams;
        const pageIndex =
            pageNumber && pageNumber.match(REGEX_DIGITS_ONLY_PATTERN) !== null && !pageNumber.startsWith("0")
                ? pageNumber
                : "";
        const generatedSearchParams = generateSearchParams({ pageIndex, ...validFilters });
        return { nonFilterSearchParams, generatedSearchParams };
    };

    const generateSearchParams = (filters: SearchParamTypes) => {
        const data = checkFilterValidity(filters);
        const searchParamData: URLSearchParamsInit | undefined = {
            ...(data?.pageIndex ? { pageIndex: data.pageIndex } : { pageIndex: "1" }),
            ...(data?.altIdentificationNo ? { altIdentificationNo: data?.altIdentificationNo } : {}),
            ...(data?.dateTime?.fromTime ? { fromTime: data?.dateTime?.fromTime } : {}),
            ...(data?.dateTime?.toTime ? { toTime: data?.dateTime?.toTime } : {}),
            ...(data?.dateTime?.range && data?.dateTime?.range !== pickerRanges.DateRange
                ? { range: data?.dateTime?.range }
                : {}),
            ...(data?.groupId?.id ? { groupId: data?.groupId?.id } : {}),
            ...(data?.status?.id ? { status: data?.status?.id } : {}),
            ...(data?.incident ? { incident: data?.incident } : {}),
            ...(data?.idInc ? { idInc: data?.idInc } : {}),
            ...(data?.locationId?.id ? { locationId: data?.locationId?.id } : {}),
            ...(data?.assignedUserID?.user_id ? { assignedUserID: data?.assignedUserID?.user_id } : {}),
            ...(data?.preFilter?.id ? { preFilter: data?.preFilter?.id } : {}),
        };
        return searchParamData;
    };

    const onSubmit: SubmitHandler<IncidentFilterFormField> = (data) => {
        const searchParamData = generateSearchParams(data);
        onFiltersReady(false);
        setSearchParams(searchParamData);
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={1}>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Controller
                        name="altIdentificationNo"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionTextFiled
                                fullWidth
                                placeholder="Rego Number"
                                value={value}
                                onChange={(event) => {
                                    setValue("altIdentificationNo", event?.target?.value, { shouldDirty: true });
                                }}
                            />
                        )}
                    />
                </Grid>
                {(isAllLocationEnable || isRegionEnable) && (
                    <>
                        <Grid item xs={12} sm={12} md={4} lg={3}>
                            <Controller
                                name="groupId"
                                control={control}
                                render={({ field: { value } }) => (
                                    <IotVisionAutoComplete
                                        options={lgaOptions.value}
                                        value={value}
                                        onChange={(
                                            event: React.SyntheticEvent,
                                            value: AutoLabelId,
                                            reason: AutocompleteChangeReason,
                                        ) => {
                                            if (reason === "clear") {
                                                setValue(
                                                    "groupId",
                                                    {
                                                        label: "",
                                                        id: "",
                                                    },
                                                    { shouldDirty: true },
                                                );
                                            } else {
                                                setValue(
                                                    "groupId",
                                                    {
                                                        label: value?.label,
                                                        id: value?.id,
                                                    },
                                                    { shouldDirty: true },
                                                );
                                            }
                                        }}
                                        renderInput={(params: any) => (
                                            <IotVisionTextFiled {...params} placeholder={lgaOptions.name} fullWidth />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={4} lg={3}>
                            <Controller
                                name="locationId"
                                control={control}
                                render={({ field: { value } }) => (
                                    <IotVisionAutoComplete
                                        options={locationOptions.value}
                                        value={value}
                                        onChange={(
                                            event: React.SyntheticEvent,
                                            value: AutoLabelId,
                                            reason: AutocompleteChangeReason,
                                        ) => {
                                            if (reason === "clear") {
                                                setValue(
                                                    "locationId",
                                                    {
                                                        label: "",
                                                        id: "",
                                                    },
                                                    { shouldDirty: true },
                                                );
                                            } else {
                                                setValue(
                                                    "locationId",
                                                    {
                                                        label: value?.label,
                                                        id: value?.id,
                                                    },
                                                    { shouldDirty: true },
                                                );
                                            }
                                        }}
                                        renderInput={(params: any) => (
                                            <IotVisionTextFiled
                                                fullWidth
                                                {...params}
                                                placeholder={locationOptions.name}
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    </>
                )}
                {isLgaEnable && (
                    <Grid item xs={12} sm={12} md={4} lg={3}>
                        <Controller
                            name="locationId"
                            control={control}
                            render={({ field: { value } }) => (
                                <IotVisionAutoComplete
                                    options={locationOptions.value}
                                    value={value}
                                    onChange={(
                                        event: React.SyntheticEvent,
                                        value: AutoLabelId,
                                        reason: AutocompleteChangeReason,
                                    ) => {
                                        if (reason === "clear") {
                                            setValue(
                                                "locationId",
                                                {
                                                    label: "",
                                                    id: "",
                                                },
                                                { shouldDirty: true },
                                            );
                                        } else {
                                            setValue(
                                                "locationId",
                                                {
                                                    label: value?.label,
                                                    id: value?.id,
                                                },
                                                { shouldDirty: true },
                                            );
                                        }
                                    }}
                                    renderInput={(params: any) => (
                                        <IotVisionTextFiled fullWidth {...params} placeholder={locationOptions.name} />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                )}
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Controller
                        name="incident"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionAutoComplete
                                options={incidentOptions.value}
                                value={value}
                                onChange={(
                                    event: React.SyntheticEvent,
                                    value: string,
                                    reason: AutocompleteChangeReason,
                                ) => {
                                    if (reason === "clear") {
                                        setValue("incident", "", {
                                            shouldDirty: true,
                                        });
                                    } else {
                                        setValue("incident", value, {
                                            shouldDirty: true,
                                        });
                                    }
                                }}
                                renderInput={(params: any) => (
                                    <IotVisionTextFiled fullWidth {...params} placeholder={incidentOptions.name} />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Controller
                        name="status"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionAutoComplete
                                options={statusOptions.value}
                                value={value}
                                onChange={(
                                    event: React.SyntheticEvent,
                                    value: AutoLabelId,
                                    reason: AutocompleteChangeReason,
                                ) => {
                                    if (reason === "clear") {
                                        setValue(
                                            "status",
                                            {
                                                label: "",
                                                id: "",
                                            },
                                            { shouldDirty: true },
                                        );
                                    } else {
                                        setValue(
                                            "status",
                                            {
                                                label: value?.label,
                                                id: value?.id,
                                            },
                                            { shouldDirty: true },
                                        );
                                    }
                                }}
                                renderInput={(params: any) => (
                                    <IotVisionTextFiled fullWidth {...params} placeholder={statusOptions?.name} />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <IotVisionToolTips
                        title={disableWarning || ""}
                        arrow
                        enterTouchDelay={0}
                        leaveTouchDelay={TOOLTIP_VISIBLE_TIME_ON_MOBILE}
                        PopperProps={{ disablePortal: true }}
                        onClose={() => handleTooltipStatus(false)}
                        open={tooltipOpen}
                    >
                        <StyledDisableWrapper
                            component="span"
                            isDisabled={!!disableWarning}
                            ref={wrapperRef}
                            onMouseEnter={() => handleTooltipStatus(true)}
                            onMouseLeave={() => handleTooltipStatus(false)}
                            onPointerDown={() => handleTooltipStatus(!tooltipOpen)}
                        >
                            <StyledBoxWrapper isDisabled={!!disableWarning}>
                                <Controller
                                    name="dateTime"
                                    control={control}
                                    render={() => (
                                        <CommonDateRangePicker
                                            value={pickerValue}
                                            disabled={!!disableWarning}
                                            onPickerMenuClose={() => {
                                                setPickerValue({
                                                    ...pickerValue,
                                                });
                                            }}
                                            onDatePickerApply={(startTime, endTime, range) => {
                                                setPickerValue({
                                                    fromTime: startTime,
                                                    toTime: endTime,
                                                    range,
                                                });
                                                setValue("dateTime.fromTime", startTime, {
                                                    shouldDirty: true,
                                                });
                                                setValue("dateTime.toTime", endTime);
                                                setValue("dateTime.range", range);
                                            }}
                                            onDatePickerClear={() => {
                                                setValue("dateTime.fromTime", "", {
                                                    shouldDirty: true,
                                                });
                                                setValue("dateTime.toTime", "");
                                                setValue("dateTime.range", pickerRanges.DateRange);
                                                setPickerValue({
                                                    fromTime: "",
                                                    toTime: "",
                                                    range: pickerRanges.DateRange,
                                                });
                                            }}
                                            buttonText="Clear"
                                        />
                                    )}
                                />
                            </StyledBoxWrapper>
                        </StyledDisableWrapper>
                    </IotVisionToolTips>
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Controller
                        name="idInc"
                        control={control}
                        rules={{
                            required: false,
                            maxLength: {
                                value: 9,
                                message: "9 digits can be allowed",
                            },
                            pattern: {
                                value: /^\d+$/,
                                message: "Should be integer",
                            },
                        }}
                        render={({ field: { value }, fieldState: { invalid, error } }) => (
                            <IotVisionTextFiled
                                fullWidth
                                placeholder="Incident ID"
                                error={invalid}
                                value={value}
                                label={error?.message}
                                onChange={(event) => {
                                    setValue("idInc", event?.target?.value, {
                                        shouldDirty: true,
                                    });
                                    // eslint-disable-next-line no-unused-expressions
                                    invalid && trigger("idInc");
                                }}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Controller
                        name="assignedUserID"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionAutoComplete
                                PopperComponent={(params) => <StyledAutocompletePopper {...params} />}
                                getOptionLabel={(option) => (option.name ? option.name : "")}
                                value={value}
                                options={assigneeOptions.value}
                                onChange={(
                                    event: React.SyntheticEvent,
                                    value: AutoLabelAssignee,
                                    reason: AutocompleteChangeReason,
                                ) => {
                                    if (reason === "clear") {
                                        setValue(
                                            "assignedUserID",
                                            {
                                                name: "",
                                                user_id: "",
                                                email: "",
                                                picture: "",
                                            },
                                            { shouldDirty: true },
                                        );
                                    } else {
                                        setValue("assignedUserID", value, {
                                            shouldDirty: true,
                                        });
                                    }
                                }}
                                renderInput={(params: any) => (
                                    <IotVisionTextFiled fullWidth {...params} placeholder={assigneeOptions.name} />
                                )}
                                renderOption={(props, option) => (
                                    <Box
                                        component="li"
                                        sx={{
                                            "& > img": {
                                                mr: 2,
                                                flexShrink: 0,
                                            },
                                        }}
                                        {...props}
                                        key={option.user_id}
                                    >
                                        <Avatar alt="profile" src={option.picture} />
                                        <div
                                            style={{
                                                marginLeft: "8px",
                                            }}
                                        >
                                            {option.name}
                                            <br />

                                            <span
                                                style={{
                                                    fontSize: "12px",
                                                }}
                                            >
                                                {option.email}
                                            </span>
                                        </div>
                                    </Box>
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Controller
                        name="preFilter"
                        control={control}
                        render={({ field: { value } }) => (
                            <IotVisionAutoComplete
                                options={presetFilterOptions.value}
                                value={value}
                                onChange={(
                                    event: React.SyntheticEvent,
                                    newValue: AutoLabelId,
                                    reason: AutocompleteChangeReason,
                                ) => {
                                    if (reason === "clear") {
                                        const data = { label: "", id: "" };
                                        setValue("preFilter", data, { shouldDirty: true });
                                        handlePreFilterSelection(data);
                                    } else {
                                        const data = { label: newValue?.label, id: newValue?.id };
                                        setValue("preFilter", data, { shouldDirty: true });
                                        handlePreFilterSelection(data);
                                    }
                                }}
                                renderInput={(params: any) => (
                                    <IotVisionTextFiled fullWidth {...params} placeholder={presetFilterOptions?.name} />
                                )}
                            />
                        )}
                    />
                </Grid>
                <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Grid container spacing={1}>
                        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <IotVisionButton
                                type="submit"
                                fullWidth
                                disabled={!isDirty || !isValid}
                                sx={{
                                    height: "37px",
                                    color: "white !important",
                                }}
                            >
                                Search
                            </IotVisionButton>
                        </Grid>
                        <Grid item xs={6} sm={6} md={6} lg={6} xl={6}>
                            <IotVisionButton
                                fullWidth
                                sx={{
                                    height: "37px",
                                    backgroundColor: "white",
                                    color: "#6b439d !important",
                                    "&:hover": {
                                        background: "white",
                                    },
                                }}
                                onClick={handleClear}
                            >
                                Clear
                            </IotVisionButton>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </form>
    );
};

export default IncidentsPageFilterPanel;
